import React, { useState, useRef, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Gavel,
  ExternalLink,
  Bookmark,
} from "lucide-react";
import axios from "axios";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api.ts";
import BidInformation from "../BidInformation";
import placeholder from "../../assets/Car-place-holder-PNG.png";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import VehicleCard from "../VehicleCard.jsx";
import { useMemo } from "react";

dayjs.extend(utc);
dayjs.extend(timezone);

const DUBAI_TZ = "Asia/Dubai";

// ─── Session-level countdown timer component ────────────────────────────────
// startDate: auction start (may be in the past for live auctions)
// endDate:   auction end (count down to this if present; else show elapsed)
// function SessionCountdown({ startDate, endDate, onModeChange }) {
//   const [display, setDisplay] = useState({ hours: '00', minutes: '00', seconds: '00', days: 0 });
//   const [mode, setMode] = useState('loading'); // 'upcoming' | 'ending' | 'elapsed' | 'ended'

//   useEffect(() => {
//     if (!startDate) return;

//     const update = () => {
//       const now = dayjs().tz(DUBAI_TZ);
//       const start = dayjs.utc(startDate).tz(DUBAI_TZ);
//       const end = endDate ? dayjs.utc(endDate).tz(DUBAI_TZ) : null;

//       // Auction hasn't started yet → count down to start
//       if (now.isBefore(start)) {
//         const diffMs = start.diff(now);
//         const totalSeconds = Math.floor(diffMs / 1000);
//         setMode('upcoming');
//         setDisplay({
//           days: Math.floor(totalSeconds / 86400),
//           hours: String(Math.floor((totalSeconds % 86400) / 3600)).padStart(2, '0'),
//           minutes: String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0'),
//           seconds: String(totalSeconds % 60).padStart(2, '0'),
//         });
//         return;
//       }

//       // Auction has started — check if endDate is future
//       if (end && now.isBefore(end)) {
//         const diffMs = end.diff(now);
//         const totalSeconds = Math.floor(diffMs / 1000);
//         setMode('ending');
//         setDisplay({
//           days: Math.floor(totalSeconds / 86400),
//           hours: String(Math.floor((totalSeconds % 86400) / 3600)).padStart(2, '0'),
//           minutes: String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0'),
//           seconds: String(totalSeconds % 60).padStart(2, '0'),
//         });
//         return;
//       }

//       // No endDate (or already ended) — show elapsed since start
//       if (end && now.isAfter(end)) {
//         setMode('ended');
//         setDisplay({ days: 0, hours: '00', minutes: '00', seconds: '00' });
//         return;
//       }

//       // Live with no endDate → show elapsed
//       const diffMs = now.diff(start);
//       const totalSeconds = Math.floor(diffMs / 1000);
//       setMode('elapsed');
//       setDisplay({
//         days: Math.floor(totalSeconds / 86400),
//         hours: String(Math.floor((totalSeconds % 86400) / 3600)).padStart(2, '0'),
//         minutes: String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0'),
//         seconds: String(totalSeconds % 60).padStart(2, '0'),
//       });
//     };

//     update();
//     const interval = setInterval(update, 1000);
//     return () => clearInterval(interval);
//   }, [startDate, endDate]);

//   // Notify parent whenever mode changes
//   useEffect(() => {
//     if (onModeChange && mode !== 'loading') {
//       onModeChange(mode);
//     }
//   }, [mode, onModeChange]);

//   if (mode === 'ended') {
//     return (
//       <div className="flex items-center gap-3 bg-[#16212b] px-4 py-2 rounded-md border border-white/5">
//         <span className="text-blue-400 text-[10px] font-black tracking-widest uppercase">SESSION ENDED</span>
//       </div>
//     );
//   }

//   let label;

//   if (mode === 'upcoming') {
//     label = 'SESSION STARTS';
//   } else if (mode === 'ending') {
//     label = 'SESSION ENDS';
//   } else if (mode === 'live') {
//     label = 'SESSION LIVE';
//   } else {
//     label = 'SESSION NOT LIVE';
//   }
// console.log("display days" , display.days , "hours :" , display.days , "mintes :" ,  display.minutes);
//   return (
//     <div className="flex items-center gap-3 bg-[#16212b] px-4 py-2 rounded-md border border-white/5">
//       <span className="text-blue-400 text-[10px] font-black tracking-widest uppercase">
//         {label}
//       </span>
//       <div className="flex items-center gap-1.5 text-blue-500 font-bold">
//         <Clock className="w-4 h-4" />
//         {display.days > 0 && (
//           <>
//             <span className="text-lg">{String(display.days).padStart(2, '0')}</span>
//             <span className="text-white/20">D</span>
//             <span className="text-white/20 mx-0.5">:</span>
//           </>
//         )}
//         <span className="text-lg">{display.hours}</span>

//         <span className="text-white/20">H</span>
//         <span className="text-white/20 mx-0.5">:</span>
//         <span className="text-lg">{display.minutes}</span>
//         <span className="text-white/20">M</span>
//         <span className="text-white/20 mx-0.5">:</span>
//         <span className="text-lg">{display.seconds}</span>
//         <span className="text-white/20">s</span>
//       </div>
//     </div>
//   );
// }

function SessionCountdown({
  startDate,
  endDate,
  onModeChange,
  translate,
  isRTL,
}) {
  const [display, setDisplay] = useState({
    days: 0,
    hours: "00",
    minutes: "00",
    seconds: "00",
  });
  const [mode, setMode] = useState("loading"); // 'upcoming' | 'ending' | 'elapsed' | 'ended'

  useEffect(() => {
    if (!startDate || isNaN(new Date(startDate).getTime())) {
      setMode("noSession");
      return;
    }

    const update = () => {
      const now = dayjs().tz(DUBAI_TZ);
      const start = dayjs.utc(startDate).tz(DUBAI_TZ);
      const end = endDate ? dayjs.utc(endDate).tz(DUBAI_TZ) : null;
      // console.log("SessionCountdown update", {
      //   now: now.format(),
      //   start: start.format(),
      //   end: end ? end.format() : null,
      // });

      let diffMs;
      if (now.isBefore(start)) {
        // Auction not started → countdown to start
        diffMs = start.diff(now);
        setMode("upcoming");
      } else if (end && now.isBefore(end)) {
        // Auction live → countdown to end
        diffMs = end.diff(now);
        setMode("ending");
      } else if (end && now.isAfter(end)) {
        // Auction ended
        setMode("ended");
        setDisplay({ days: 0, hours: "00", minutes: "00", seconds: "00" });
        return;
      } else {
        // Live with no end → elapsed since start
        diffMs = now.diff(start);
        setMode("elapsed");
      }

      const totalSeconds = Math.floor(diffMs / 1000);
      setDisplay({
        days: Math.floor(totalSeconds / 86400),
        hours: String(Math.floor((totalSeconds % 86400) / 3600)).padStart(
          2,
          "0",
        ),
        minutes: String(Math.floor((totalSeconds % 3600) / 60)).padStart(
          2,
          "0",
        ),
        seconds: String(totalSeconds % 60).padStart(2, "0"),
      });
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startDate, endDate]);

  // Notify parent
  useEffect(() => {
    if (onModeChange && mode !== "loading") onModeChange(mode);
  }, [mode, onModeChange]);

  let label;
  if (mode === "upcoming")
    label = translate("newHome.auctions.startIn") || "SESSION STARTS IN";
  else if (mode === "ending")
    label = translate("newHome.auctions.endsIn") || "SESSION ENDS IN";
  else if (mode === "elapsed")
    label = translate("newHome.auctions.liveFrom") || "SESSION LIVE FROM";
  else label = translate("newHome.auctions.ended") || "SESSION ENDED";

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className={`bg-[#16212b] px-4 py-2 rounded-md border border-white/5 flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3
                  ${isRTL ? "lg:pl-6 lg:pr-4" : "lg:pr-6 lg:pl-4"}`} // Adjust padding based on RTL
    >
      {/* Label */}
      <span className="text-blue-400 text-xs font-black tracking-widest uppercase text-center lg:text-left">
        {label}
      </span>

      {/* Clock / Timer */}
      {mode !== "noSession" && mode !== "ended" ? (
        <div className="flex items-center justify-center lg:justify-start gap-1 text-blue-500 font-bold flex-wrap">
          <Clock className="w-5 h-5" />

          {display.days > 0 && (
            <>
              <span className="w-6 text-center text-lg">
                {String(display.days).padStart(2, "0")}
              </span>
              <span className="text-white/50 text-sm text-center">D</span>
              <span className="text-white/50 mx-0.5">:</span>
            </>
          )}

          <span className="w-6 text-center text-lg">
            {String(display.hours).padStart(2, "0")}
          </span>
          <span className="text-white/50 text-sm text-center">H</span>
          <span className="text-white/50 mx-0.5">:</span>

          <span className="w-6 text-center text-lg">
            {String(display.minutes).padStart(2, "0")}
          </span>
          <span className="text-white/50 text-sm text-center">M</span>
          <span className="text-white/50 mx-0.5">:</span>

          <span className="w-6 text-center text-lg">
            {String(display.seconds).padStart(2, "0")}
          </span>
          <span className="text-white/50 text-sm text-center">S</span>
        </div>
      ) : (
        <p className="text-white text-sm font-medium mt-1 lg:mt-0">
          {mode === "noSession"
            ? translate("newHome.auctions.noLive") ||
            "No live auction at the moment."
            : translate("newHome.auctions.ended") || "Auction ended."}
        </p>
      )}
    </div>
  );
}
// ────────────────────────────────────────────────────────────────────────────

export default function Auctions() {
  const { language, translate } = useLanguage();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const isRTL = language === "ar";

  const scrollRef = useRef(null);
  const [activePage, setActivePage] = useState(0);

  const [auctions, setAuctions] = useState([]);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [laneData, setLaneData] = useState({});
  const [activeLane, setActiveLane] = useState("");
  const [liveAuction, setLiveAuction] = useState([]);
  const [isBookmarked, setIsBookmarked] = useState({});
  const [loading, setLoading] = useState(true);

  // Per-vehicle countdowns (for cards)
  const [countdowns, setCountdowns] = useState({});
  const [auctionMode, setAuctionMode] = useState("notLive");

  // Pre-bid modal states
  const [showBidNowPopup, setShowBidNowPopup] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [bidAmount, setBidAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showAllLanes, setShowAllLanes] = useState(true);

  const totalLaneVehicles = useMemo(() => {
    return Object.values(laneData || {}).reduce((total, laneVehicles) => {
      return total + (Array.isArray(laneVehicles) ? laneVehicles.length : 0);
    }, 0);
  }, [laneData]);



  // 1. Load user's watchlist / bookmarks
  useEffect(() => {
    const fetchWishlist = async () => {
      if (!user?.id) return;
      try {
        const response = await api.get("/wishlist");
        const data = response.data?.data || response.data || [];
        const bookmarks = {};
        data.forEach((item) => {
          if (item.vehicleId) bookmarks[item.vehicleId] = true;
        });
        setIsBookmarked(bookmarks);
      } catch (err) {
        console.error("Failed to load wishlist", err);
      }
    };
    fetchWishlist();
  }, [user?.id]);

  // Prevent body scroll when bid popup is open
  useEffect(() => {
    if (showBidNowPopup) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showBidNowPopup]);

  // 2. Fetch LIVE auctions only
  useEffect(() => {
    const fetchAuctions = async () => {
      setLoading(true);
      try {
        // Fetch live, upcoming and closed auctions
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/auctions/paginated`,
          {
            params: {
              page: 1,
              limit: 50,
              orderBy: "startDate",
              order: "ASC",
              type: "live,upcomming,closed",
              filters: { unSoldVehicles: 1, isActive: true },
            },
          },
        );

        const allAuctions = res.data.data || [];
        setLiveAuction(allAuctions);

        // Map and filter: only auctions with vehicles
        const mappedAuctions = allAuctions
          .filter((a) => a && a.auctionLanes?.length > 0)
          .map((a) => {
            const lanes = a.auctionLanes.map((lane) => ({
              id: lane.id,
              name: lane.lane?.name || `Lane ${lane.id}`,
              vehicles: lane.auctionLaneVehicles || [],
            }));

            return {
              id: a.id,
              name: a.name || `Auction ${a.id}`,
              startDate: a.startDate,
              endDate: a.endDate || a.endTime || null,
              branch: a.branch?.name || "Unknown",
              type: a.type,
              auctionLanes: lanes,
              totalVehicles: lanes.reduce(
                (sum, l) => sum + l.vehicles.length,
                0,
              ),
            };
          })
          .filter((a) => a.totalVehicles > 0);

        // Sort: Live first, then Upcoming, then Closed
        const now = dayjs().tz(DUBAI_TZ);
        const sortedAuctions = mappedAuctions.sort((a, b) => {
          const isAuctionLive = (auc) => {
            const start = dayjs.utc(auc.startDate).tz(DUBAI_TZ);
            const end = auc.endDate
              ? dayjs.utc(auc.endDate).tz(DUBAI_TZ)
              : null;
            return (
              (now.isAfter(start) || now.isSame(start)) &&
              (!end || now.isBefore(end))
            );
          };
          const isAuctionUpcoming = (auc) =>
            dayjs.utc(auc.startDate).tz(DUBAI_TZ).isAfter(now);

          const aLive = isAuctionLive(a);
          const bLive = isAuctionLive(b);
          if (aLive && !bLive) return -1;
          if (!aLive && bLive) return 1;

          const aUp = isAuctionUpcoming(a);
          const bUp = isAuctionUpcoming(b);
          if (aUp && !bUp) return -1;
          if (!aUp && bUp) return 1;

          return new Date(a.startDate) - new Date(b.startDate);
        });

        setAuctions(sortedAuctions);

        // Auto-select first valid auction
        if (sortedAuctions.length > 0) {
          setSelectedAuction(sortedAuctions[0]);
        }
      } catch (err) {
        console.error("Failed to load auctions", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAuctions();
  }, []);

  // 3. Prepare lanes when selected auction changes
  useEffect(() => {
    if (!selectedAuction?.auctionLanes) {
      setLaneData({});
      setActiveLane("");
      setShowAllLanes(true);
      return;
    }

    const lanes = {};
    selectedAuction.auctionLanes.forEach((lane) => {
      lanes[lane.name] = lane.vehicles.map((v) => ({
        ...v,
        startDate: selectedAuction.startDate,
        auctionLaneId: lane.id,
      }));
    });

    setLaneData(lanes);
    setShowAllLanes(true);
    setActiveLane("All Lanes");
    setActivePage(0);
  }, [selectedAuction]);

  //   useEffect(() => {
  //   if (auctions.length === 0) return;

  //   // Find the soonest future (or current) start date
  //   let soonest = null;

  //   auctions.forEach(a => {
  //     const sd = dayjs(a.startDate).tz(DUBAI_TZ);
  //     if (!soonest || sd.isBefore(soonest)) {
  //       soonest = sd;
  //     }
  //   });

  //   if (soonest) {
  //     setEarliestStart(soonest);
  //   }
  // }, [auctions]);

  useEffect(() => {
    if (!activeLane || !laneData[activeLane]?.length) return;

    const update = () => {
      const timers = {};
      laneData[activeLane].forEach((item) => {
        const vehicle = item.vehicle || {};
        const startDate = item.startDate || selectedAuction?.startDate;

        if (!startDate) {
          timers[vehicle.id] = { hours: "00", minutes: "00", seconds: "00" };
          return;
        }

        const diffMs = dayjs
          .utc(startDate)
          .tz(DUBAI_TZ)
          .diff(dayjs().tz(DUBAI_TZ));

        if (diffMs <= 0) {
          timers[vehicle.id] = { hours: "00", minutes: "00", seconds: "00" };
        } else {
          const totalSeconds = Math.floor(diffMs / 1000);
          timers[vehicle.id] = {
            hours: String(Math.floor(totalSeconds / 3600)).padStart(2, "0"),
            minutes: String(Math.floor((totalSeconds % 3600) / 60)).padStart(
              2,
              "0",
            ),
            seconds: String(totalSeconds % 60).padStart(2, "0"),
          };
        }
      });
      setCountdowns(timers);
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [activeLane, laneData, selectedAuction?.startDate]);

  // 4. Countdown timer (updated every second)
  // useEffect(() => {
  //   if (!activeLane || !laneData[activeLane]?.length) return;

  //   const interval = setInterval(() => {
  //     const timers = {};
  //     laneData[activeLane].forEach(item => {
  //       const end = item.startDate;
  //       if (!end) return;

  //       const diffMs = dayjs(end).tz(DUBAI_TZ).diff(dayjs().tz(DUBAI_TZ));
  //       if (diffMs <= 0) return;

  //       const hours = Math.floor(diffMs / 3600000);
  //       const minutes = Math.floor((diffMs % 3600000) / 60000);
  //       const seconds = Math.floor((diffMs % 60000) / 1000);

  //       const vehicleId = item.vehicle?.id;
  //       if (vehicleId) {
  //         timers[vehicleId] = {
  //           hours: String(hours).padStart(2, '0'),
  //           minutes: String(minutes).padStart(2, '0'),
  //           seconds: String(seconds).padStart(2, '0'),
  //         };
  //       }
  //     });
  //     setCountdowns(timers);
  //   }, 1000);

  //   return () => clearInterval(interval);
  // }, [activeLane, laneData]);

  // Scroll controls
  const handleScroll = (direction) => {
    if (!scrollRef.current) return;
    const { scrollLeft, clientWidth } = scrollRef.current;
    const target =
      direction === "left"
        ? scrollLeft - clientWidth
        : scrollLeft + clientWidth;
    scrollRef.current.scrollTo({ left: target, behavior: "smooth" });
  };

  // const handleScrollProgress = () => {
  //   if (!scrollRef.current) return;
  //   const index = Math.round(
  //     scrollRef.current.scrollLeft / scrollRef.current.clientWidth,
  //   );
  //   setActivePage(index);
  // };
  const VEHICLES_PER_PAGE = 4;

  const vehicles = useMemo(() => {
    if (showAllLanes) {
      return Object.values(laneData || {}).flat();
    }

    return laneData[activeLane] || [];
  }, [showAllLanes, laneData, activeLane]);

  const totalPages = Math.ceil(vehicles.length / VEHICLES_PER_PAGE);

  // Handle scroll progress
  const handleScrollProgress = () => {
    if (!scrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

    // Compute page index correctly based on VEHICLES_PER_PAGE
    const maxScrollLeft = scrollWidth - clientWidth;
    const scrollFraction = scrollLeft / maxScrollLeft;

    // Multiply fraction by (totalPages - 1) to get 0-based page index
    let index = Math.round(scrollFraction * (totalPages - 1));

    // Clamp index
    index = Math.max(0, Math.min(index, totalPages - 1));

    setActivePage(index);
  };


  // ── Bid Now logic ────────────────────────────────────────
  const handleBidNow = (vehicle) => {
    let auctionLaneData = null;

    for (const auction of liveAuction) {
      for (const lane of auction.auctionLanes || []) {
        const found = lane.auctionLaneVehicles?.find(
          (av) => av.vehicleId === vehicle.id,
        );
        if (found) {
          auctionLaneData = {
            id: found.id,
            auctionLane: { id: lane.id, name: lane.lane?.name },
            runNo: found.runNo,
          };
          break;
        }
      }
      if (auctionLaneData) break;
    }

    const vehicleWithData = {
      ...vehicle,
      auctionLanesVehicles: auctionLaneData ? [auctionLaneData] : [],
    };

    setSelectedVehicle(vehicleWithData);
    setShowBidNowPopup(true);

    const current = parseFloat(vehicle?.latestBid) || 0;
    const base = current > 0 ? current : vehicle?.startingBid || 0;
    setBidAmount(base + (vehicle?.bidIncrement || 100));
  };

  const handleBidNowSubmit = async () => {
    setIsSubmitting(true);
    try {
      showSuccess("Bid submitted successfully!");
    } catch (err) {
      showError("Failed to place bid");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeBidNowPopup = () => {
    setShowBidNowPopup(false);
    setSelectedVehicle(null);
  };

  // Navigate to Search page (vehicles) with optional auctionId
  const handleViewAll = () => {
    if (selectedAuction?.id) {
      navigate(`/vehicles?auctionId=${selectedAuction.id}`);
    } else {
      navigate("/vehicles");
    }
  };


  // Loading state
  if (loading) {
    return (
      <section className="bg-bg-light py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-20">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </section>
    );
  }

  // No live auctions
  if (auctions.length === 0) {
    return null;
  }

  return (
    <section className="bg-bg-medium py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10 flex flex-col gap-6">
          <div className="flex items-center gap-3">
            <div className="h-[2px] w-12 bg-blue-500" />
            <p className="text-blue-500 text-xs font-bold tracking-[0.2em] uppercase">
              {translate("newHome.auctions.premiumInventory") ||
                "PREMIUM INVENTORY"}
            </p>
          </div>

          {/* Title + dynamic countdown + arrows + view all */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                {selectedAuction
                  ? selectedAuction.name || `Auction #${selectedAuction.id}`
                  : translate("newHome.auctions.currentGlobalAuctions") ||
                  "Current Global Auctions"}
              </h2>

              {/* <div className="flex items-center gap-3 bg-[#16212b] px-4 py-2 rounded-md border border-white/5">
                <span className="text-blue-400 text-[10px] font-black tracking-widest uppercase">
                  SESSION STARTS
                </span>
                <div className="flex items-center gap-1.5 text-blue-500 font-bold">
                  <Clock className="w-4 h-4" />
                  <span className="text-lg">04</span><span className="text-white/20">H</span>
                  <span className="text-white/20 mx-0.5">:</span>
                  <span className="text-lg">22</span><span className="text-white/20">M</span>
                  <span className="text-white/20 mx-0.5">:</span>
                  <span className="text-lg">15</span><span className="text-white/20">s</span>
                </div>
              </div> */}

              {/* Dynamic countdown timer — shows STARTS IN / ENDS IN / LIVE FOR */}
              {selectedAuction?.startDate && (
                <SessionCountdown
                  startDate={selectedAuction.startDate}
                  endDate={selectedAuction.endDate}
                  onModeChange={setAuctionMode}
                  translate={translate} // Pass your translation function
                  isRTL={isRTL}
                />
              )}
            </div>
          </div>

          {(() => {
            if (!selectedAuction?.startDate) return null;

            const now = dayjs().tz(DUBAI_TZ);
            const start = dayjs.utc(selectedAuction.startDate).tz(DUBAI_TZ);
            const endDateAttr =
              selectedAuction.endDate || selectedAuction.endTime;
            const end = endDateAttr
              ? dayjs.utc(endDateAttr).tz(DUBAI_TZ)
              : null;

            const isStarted = now.isAfter(start) || now.isSame(start);
            const isEnded = end ? now.isAfter(end) : false;
            const isLive = isStarted && !isEnded;

            return (
              <div
                dir={isRTL ? "rtl" : "ltr"}
                className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 sm:gap-4"
              >
                {/* LEFT SIDE */}
                <div className="flex flex-wrap items-center gap-1 sm:gap-4">
                  <div
                    className={`flex items-center gap-1 sm:gap-2 border px-2 py-1 rounded-full text-[10px] sm:text-[10px] ${isLive
                      ? "bg-[#142d18] border-green-900/30"
                      : "bg-[#414223] border-yellow-900/30"
                      }`}
                  >
                    <div
                      className={`w-1.5 h-1.5 rounded-full animate-pulse ${isLive ? "bg-green-500" : "bg-yellow-500"
                        }`}
                    />
                    <span
                      className={`font-black tracking-widest ${isLive ? "text-green-500" : "text-yellow-500"
                        }`}
                    >
                      {isLive ? "LIVE NOW" : isEnded ? "ENDED" : "UPCOMING"}
                    </span>
                  </div>

                  <p className="text-gray-500 text-[10px] sm:text-sm font-medium">
                    {translate("newHome.auctions.realTimeBidding")}
                  </p>
                </div>

                {/* CENTER FILTERS: Lanes */}
                {Object.keys(laneData || {}).length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                     <button
                      onClick={() => navigate("/auctions")}
                      className="px-3.5 py-1.5 rounded-md text-[10px] sm:text-xs font-black tracking-wide border bg-green-600 text-white border-green-500 hover:bg-green-700 transition-all shadow-[0_0_18px_rgba(34,197,94,0.25)]"
                    >
                      View Auctions
                    </button> 
                    <button
                      onClick={() => {
                        setShowAllLanes(true);
                        setActiveLane("All Lanes");
                        setActivePage(0);
                      }}
                      className={`px-3.5 py-1.5 rounded-md text-[10px] sm:text-xs font-black tracking-wide border transition-all ${showAllLanes
                          ? "bg-blue-600 text-white border-blue-500 shadow-[0_0_18px_rgba(37,99,235,0.25)]"
                          : "bg-[#16212b] text-gray-400 border-white/10 hover:text-white hover:border-blue-500/50 hover:bg-[#1b2a3a]"
                        }`}
                    >
                      All Lanes ({totalLaneVehicles})
                    </button>

                    {Object.keys(laneData).sort().map((lane) => (
                      <button
                        key={lane}
                        onClick={() => {
                          setActiveLane(lane);
                          setShowAllLanes(false);
                          setActivePage(0);
                        }}
                        className={`px-3.5 py-1.5 rounded-md text-[10px] sm:text-xs font-black tracking-wide border transition-all ${!showAllLanes && lane === activeLane
                            ? "bg-blue-600 text-white border-blue-500 shadow-[0_0_18px_rgba(37,99,235,0.25)]"
                            : "bg-[#16212b] text-gray-400 border-white/10 hover:text-white hover:border-blue-500/50 hover:bg-[#1b2a3a]"
                          }`}
                      >
                        {lane} ({laneData[lane]?.length || 0})
                      </button>
                    ))}      
                  </div>
                )}

                {/* RIGHT SIDE */}
                <div
                  className={`flex items-center gap-2 sm:gap-4 px-5 ${isRTL
                    ? "justify-start lg:justify-end lg:pr-6"
                    : "justify-end lg:justify-end"
                    }`}
                >
                  {/* Scroll Buttons */}
                  <button
                    onClick={() => handleScroll("left")}
                    className="p-2.5 rounded-full border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  <button
                    onClick={() => handleScroll("right")}
                    className="p-2.5 rounded-full border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>

                  {/* View All */}
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 text-primary font-bold text-xs sm:text-sm hover:underline bg-transparent border-0 cursor-pointer"
                  >
                    {translate("newHome.auctions.viewAll") || "View All"}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Lane tabs */}
          {/* {Object.keys(laneData).length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {Object.keys(laneData).sort().map(laneName => (
                <button
                  key={laneName}
                  onClick={() => setActiveLane(laneName)}
                  className={`px-4 py-1.5 text-xs rounded-full font-semibold border whitespace-nowrap transition-colors ${activeLane === laneName
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-white/10 text-gray-400 hover:text-white hover:border-white/30'
                    }`}
                >
                  {laneName}
                </button>
              ))}
            </div>
          )} */}
        </div>

        {/* Horizontal scroll vehicle list */}
        <div
          ref={scrollRef}
          onScroll={handleScrollProgress}
          className="flex gap-5 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 px-5"
        >
          {vehicles.length === 0 ? (
            <div className="min-w-full text-center py-20 text-gray-500">
              No vehicles available in this lane right now
            </div>
          ) : (
            vehicles.map((item) => {
              const vehicle = { ...(item.vehicle || {}), runNo: item.runNo };
              const timer = countdowns[vehicle.id];

              return (
                <div
                  key={item.id}
                  className="w-full sm:w-[calc(50%-10px)] lg:w-[calc(20%-12px)] flex-shrink-0 snap-start  rounded-xl overflow-hidden transition-all group"
                >
                  <VehicleCard
                    vehicle={vehicle}
                    wishlistData={Object.keys(isBookmarked).map((id) => ({
                      vehicleId: id,
                    }))}
                    countdown={timer}
                    // auctionLaneId={
                    //   item.vehicle.auctionLanesVehicles?.[0]?.auctionLane?.id
                    // }
                    auctionLaneId={item.auctionLaneId}
                    auction={selectedAuction}
                    onBidNow={() => handleBidNow(vehicle)}
                    onBookmarkToggle={(vehicleId) => {
                      setIsBookmarked((prev) => ({
                        ...prev,
                        [vehicleId]: !prev[vehicleId],
                      }));
                    }}
                  />

                  {/* <div className="aspect-[4/3] relative overflow-hidden">
                    <img
                      src={vehicle.imageUrl || vehicle.images?.[0]?.imageUrl || placeholder}
                      alt={vehicle.name || 'Vehicle'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />

                    <div className="absolute top-3 left-3 bg-green-600/90 text-white px-2.5 py-1 rounded text-xs font-bold flex items-center gap-1.5 shadow-md">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      LIVE
                    </div>

                    {timer && (
                      <div className="absolute bottom-3 left-3 bg-black/70 text-white px-3 py-1.5 rounded text-sm font-mono flex items-center gap-1.5">
                        <Clock size={16} />
                        {timer.hours}:{timer.minutes}:{timer.seconds}
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-white font-bold text-lg line-clamp-2">
                        {vehicle.name || 'Unknown Vehicle'}
                      </h3>
                  
                    </div>

                    <p className="text-gray-500 text-xs uppercase mb-4">
                      {selectedAuction?.branch || '—'}
                    </p>

                    <div className="grid grid-cols-4 gap-3  mb-6 border-y border-white/10 py-3 text-gray-400 text-xs">
                      <div>{vehicle.year || '—'}</div>
                      <div>{vehicle.mileage ? `${vehicle.mileage.toLocaleString()} km` : '—'}</div>
                      <div>{vehicle.fuelType?.name || '—'}</div>
                      <div>{vehicle.TransmissionType?.name || '—'}</div>
                    </div>

                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-gray-500 text-xs uppercase mb-1">Current Bid</p>
                        <p className="text-white font-bold text-2xl">
                          {vehicle.latestBid
                            ? `$${Number(vehicle.latestBid).toLocaleString()}`
                            : vehicle.startingBid
                            ? `$${Number(vehicle.startingBid).toLocaleString()}`
                            : '—'}
                        </p>
                      </div>

                      <button
                        onClick={() => handleBidNow(vehicle)}
                        disabled={isSubmitting}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-60"
                      >
                        <Gavel size={18} />
                        Bid Now
                      </button>
                    </div>
                  </div> */}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination dots */}
        {/* {vehicles.length > 5 && (
        <div className="flex justify-center gap-2 mt-10">
          {[...Array(Math.ceil(vehicles.length / 4))].map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === activePage ? "w-10 bg-blue-500" : "w-3 bg-gray-700"
                }`}
            />
          ))}
        </div>
        )} */}
        {/* Pagination dots */}
        {vehicles.length > VEHICLES_PER_PAGE && (
          <div className="flex justify-center gap-2 mt-10">
            {Array.from({ length: totalPages }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === activePage ? "w-10 bg-blue-500" : "w-3 bg-gray-700"
                  }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pre-bid modal */}
      {showBidNowPopup && selectedVehicle && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="relative bg-[#1a1f2e] rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-700/50">
            <button
              onClick={closeBidNowPopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-white bg-gray-800/60 hover:bg-gray-700 rounded-full p-2 transition-colors duration-200 z-10"
              aria-label="Close bid modal"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <BidInformation
              vehicle={selectedVehicle}
              bidAmount={bidAmount}
              setBidAmount={setBidAmount}
              isSubmittingBid={isSubmitting}
              handlePreBidSubmit={handleBidNowSubmit}
            />
          </div>
        </div>
      )}

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
}
